import type { DocumentDeleteDialogContentFragment } from "../client/generated";
import { Trans, useTranslation } from "react-i18next";
import { Button } from "@acdc2/ui/components/button";
import { AccessButton } from "@acdc2/ui/components/access-button";
import { useDeleteDocumentMutation } from "../client/generated";
import {
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@acdc2/ui/components/dialog";

type Props = {
  fragment: DocumentDeleteDialogContentFragment;
  onDone?: () => void;
};

export function DocumentDeleteDialogContent({ fragment, onDone }: Props) {
  const { t } = useTranslation();
  const [deleteDocument, { loading }] = useDeleteDocumentMutation({});

  const onSubmit = async () => {
    try {
      await deleteDocument({
        variables: { id: fragment.id },
        update(cache) {
          const id = cache.identify(fragment);
          cache.evict({ id });
          cache.gc();
        },
      });
      onDone?.();
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <DialogContent showCloseButton={false} data-testid="document-delete-dialog">
      <DialogHeader>
        <DialogTitle>{t("DocumentDeleteDialogContent.title")}</DialogTitle>
        <DialogDescription>
          <Trans
            i18nKey="DocumentDeleteDialogContent.description"
            components={{ "word-break": <span className="break-all" /> }}
            values={{ name: fragment.name }}
          />
        </DialogDescription>
      </DialogHeader>
      <DialogFooter>
        <DialogClose asChild>
          <Button type="button" variant="secondary">
            {t("DocumentDeleteDialogContent.buttons.cancel")}
          </Button>
        </DialogClose>

        <AccessButton
          variant="destructive"
          onClick={onSubmit}
          loading={loading}
          disabled={loading}
          children={t("DocumentDeleteDialogContent.buttons.confirm")}
          data-testid="document-delete-confirm-button"
        />
      </DialogFooter>
    </DialogContent>
  );
}
